import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";
import {
  resetTeacherRequestTeacherPayout,
  teacherRequestTeacherPayout,
} from "@academy-context/write/application/use-cases/teacher/teacher-payout-request/request-teacher-payout";
import { selectTeacherPayoutRequest } from "@academy-context/write/application/use-cases/teacher/teacher-payout-request/selectors/teacher-payout-request-selectors";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import { useFieldUpload } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import { teacherRetrievePayoutRequestEligibility } from "../../../read/application/use-cases/teacher/payout-request-eligibility/retrieve-payout-request-eligibility";
import { uploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { formatFormDataToBody, type PayoutRequestFormInputs } from "./form-validation/payout-request.schema";

export const usePayoutRequest = (form: ActionForm<PayoutRequestFormInputs>, onSuccess: () => void) => {
  const dispatch = useAppDispatch();
  const run = useApiAction();
  const { toast } = useToast();
  const { processing } = useAppSelector(selectTeacherPayoutRequest);
  useResetWhenSettled(processing, resetTeacherRequestTeacherPayout);
  const upload = useFieldUpload(form);

  // L'API dit pourquoi la demande est refusée (paiement automatique, aucun coaching impayé, date de facture antérieure à
  // la période comptable ouverte), sous le champ fautif quand elle le désigne
  const request = useCallback(
    async (values: PayoutRequestFormInputs) => {
      const outcome = await run(teacherRequestTeacherPayout(formatFormDataToBody(values)), {
        failure: "Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.",
      });
      if (!outcome.ok) {
        // Le body nomme la date de facture invoice.date ; le formulaire, invoiceDate
        applyApiFieldErrors(form, outcome.error, { "invoice.date": "invoiceDate" });
        return;
      }
      // Toast titré : hors de run, qui ne donne qu'une description
      toast({
        title: "Demande envoyée",
        description: "Votre demande de règlement a bien été créée, elle sera traitée sous 48h.",
      });
      onSuccess();
      dispatch(teacherRetrievePayoutRequestEligibility());
    },
    [run, dispatch, toast, form, onSuccess],
  );

  // Sans feedback : le refus du fichier s'affiche sous le champ
  const uploadInvoice = useCallback(
    (file: File) => upload("invoiceUrl", run(uploadDocument({ document: file, asAdmin: false, role: ROLES.TEACHER }))),
    [upload, run],
  );

  return { request, uploadInvoice };
};
